import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Tables } from "types/supabase";
import { apiGetRequest } from "utils/api";

export type DefaultPayments = Tables<"defaulted_loan_interest_rate"> & {
  sum_default_interest_amount: number;
};

export function useDefaultInterest({ loan_id }: { loan_id: string }) {
  const queryClient = useQueryClient();

  const {
    data: defaultPayments,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["loan_payments_default_interest", loan_id],
    queryFn: async () => {
      return await apiGetRequest<DefaultPayments[]>(
        "/supabase/loans/payments/default_interest",
        {
          loan_id,
        }
      );
    },
    enabled: !!loan_id,
  });

  const invalidate = async () =>
    await queryClient.invalidateQueries({
      queryKey: ["loan_payments_default_interest", loan_id],
    });

  const total = defaultPayments?.reduce(
    (acc, curr) => acc + curr.sum_default_interest_amount,
    0
  );

  //compare the effective_to, and effective_from dates, in DDDD-MM-YY format
  //to the current date, and return true if any of the dates are current
  const now = new Date();

  const isCurrentlyDefaulted = defaultPayments?.some((def) => {
    const effective_from = new Date(
      (def.effective_from as string) + "T00:00:00"
    );
    const effective_to = def.effective_to
      ? new Date((def.effective_to as string) + "T23:59:59")
      : now;
    return effective_from <= now && effective_to >= now;
  });

  const hasDefaulted = (defaultPayments?.length ?? 0) > 0;

  return {
    defaultPayments,
    isLoading,
    isFetching,
    total,
    isCurrentlyDefaulted,
    hasDefaulted,
    invalidate,
  };
}
